<div class="admin-dashboard p-4">
  <!-- Loading State -->
  <div *ngIf="loading" class="flex items-center justify-center min-h-screen">
    <div class="text-center">
      <p-progressSpinner styleClass="w-16 h-16" strokeWidth="3" animationDuration="1s"></p-progressSpinner>
      <p class="text-gray-600 mt-4 text-lg" i18n>Loading dashboard data...</p>
    </div>
  </div>

  <!-- Main Dashboard Content -->
  <div *ngIf="!loading">
  <!-- Header -->
  <div class="flex flex-wrap md:flex-nowrap justify-between items-center mb-6">
    <div>
      <h1 class="text-3xl font-bold text-gray-900 mb-2" i18n>SA and A Dashboard</h1>
      <p class="text-gray-600" i18n>Real-time overview of administrative activities</p>
      <!-- Date Filter Status -->
      <div class="mt-2 flex items-center gap-2">
        <i class="pi pi-calendar" [ngClass]="{'text-blue-500': isDateFilterActive, 'text-gray-500': !isDateFilterActive}"></i>
        <span class="text-sm font-medium px-3 py-1 rounded-full" 
              [ngClass]="{'text-blue-700 bg-blue-50': isDateFilterActive, 'text-gray-600 bg-gray-50': !isDateFilterActive}" 
              i18n>{{ dateFilterDescription }}</span>
        <p-button *ngIf="isDateFilterActive" appGlobalLoadingDisable icon="pi pi-times" [text]="true" size="small" severity="secondary" (onClick)="clearDateFilter()" [pTooltip]="'Clear date filter'" i18n-pTooltip></p-button>
      </div>
    </div>
    <div class="flex flex-wrap md:flex-nowrap items-center gap-3">
      <!-- Date Range Filter -->
      <div class="flex items-center gap-2">
        <label class="text-sm font-medium text-gray-700" i18n>Date Range:</label>
        <p-select 
          [options]="dateFilterOptions" 
          [(ngModel)]="selectedPeriod" 
          optionLabel="label" 
          optionValue="value"
          (onChange)="onPeriodChange()"
          placeholder="Select period" i18n-placeholder
          i18n-placeholder
          [style]="{'min-width': '140px'}"
          [showClear]="false">
        </p-select>
      </div>
      
      <!-- Custom Date Picker -->
      <div *ngIf="showCustomDatePicker" class="flex flex-wrap md:flex-nowrap items-center gap-2">
        <p-datePicker 
          [(ngModel)]="customStartDate" 
          placeholder="Start Date" 
          i18n-placeholder
          (onSelect)="onCustomDateChange()" 
          [showIcon]="true" 
          dateFormat="mm/dd/yy"
          [style]="{'min-width': '150px'}">
        </p-datePicker>
        <span class="text-gray-400">-</span>
        <p-datePicker 
          [(ngModel)]="customEndDate" 
          placeholder="End Date" 
          i18n-placeholder
          (onSelect)="onCustomDateChange()" 
          [showIcon]="true" 
          dateFormat="mm/dd/yy"
          [style]="{'min-width': '150px'}">
        </p-datePicker>
      </div>
       
    </div>
  </div>

  <!-- KPI Cards -->
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
    <!-- Loading Skeletons -->
    <div *ngIf="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 col-span-full">
      <p-card *ngFor="let item of [1,2,3,4]" class="kpi-card">
        <div class="flex items-center justify-between">
          <div class="w-full">
            <div class="flex items-center gap-2 mb-2">
              <p-skeleton shape="circle" size="2.5rem"></p-skeleton>
              <p-skeleton width="8rem" height="1rem"></p-skeleton>
            </div>
            <p-skeleton width="4rem" height="2rem" styleClass="mb-2"></p-skeleton>
          </div>
        </div>
      </p-card>
    </div>
    
    <!-- Actual KPI Cards -->
    <div *ngIf="!loading && dashboardData" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 col-span-full">
      <!-- Total Customers -->
      <p-card class="kpi-card cursor-pointer relative" 
              [ngClass]="{'border-2 border-blue-300 shadow-lg': isDateFilterActive}" 
              (click)="navigateToCustomers()"
              [pTooltip]="'View all customers and their details. Click to navigate to customer management.'"
              i18n-pTooltip
              tooltipPosition="top">
        <div class="absolute top-3 right-3 z-10" [pTooltip]="'Clickable'" i18n-pTooltip tooltipPosition="top">
          <div class="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center border border-blue-300 shadow-sm hover:bg-blue-200 transition-colors cursor-default">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#2563EB" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 1.07V9h7c0-4.08-3.05-7.44-7-7.93zM4 15c0 4.42 3.58 8 8 8s8-3.58 8-8v-4H4v4zm7-13.93C7.05 1.56 4 4.92 4 9h7V1.07z"/>
            </svg>
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div>
            <div class="flex items-center gap-2 mb-2">
              <i class="pi pi-users text-blue-500 text-xl" [pTooltip]="'Customer metrics'" i18n-pTooltip></i>
              <span class="text-sm font-medium text-gray-600" i18n>Total Customers</span>
              <i *ngIf="isDateFilterActive" 
                 class="pi pi-filter text-blue-500 text-xs ml-1" 
                 [pTooltip]="'Results filtered by selected date range'"
                 i18n-pTooltip
                 tooltipPosition="top"></i>
            </div>
            <div class="text-3xl font-bold text-gray-900">{{ dashboardData.summaryStats.totalCustomers }}</div>
          </div>
        </div>
      </p-card>

      <!-- Active FSRs (Not affected by date filter) -->
      <p-card class="kpi-card cursor-pointer relative" 
              (click)="navigateToActiveFSRs()"
              [pTooltip]="'View all Active Field Sales Representatives. Click to navigate to user management with Active FSRs filter applied.'"
              i18n-pTooltip
              tooltipPosition="top">
        <div class="absolute top-3 right-3 z-10" [pTooltip]="'Clickable'" i18n-pTooltip tooltipPosition="top">
          <div class="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center border border-blue-300 shadow-sm hover:bg-blue-200 transition-colors cursor-default">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#2563EB" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 1.07V9h7c0-4.08-3.05-7.44-7-7.93zM4 15c0 4.42 3.58 8 8 8s8-3.58 8-8v-4H4v4zm7-13.93C7.05 1.56 4 4.92 4 9h7V1.07z"/>
            </svg>
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div>
            <div class="flex items-center gap-2 mb-2">
              <i class="pi pi-user text-green-500 text-xl" [pTooltip]="'Active FSR count'" i18n-pTooltip></i>
              <span class="text-sm font-medium text-gray-600" i18n>Active FSRs</span>
              <span *ngIf="isDateFilterActive" 
                    class="text-xs text-gray-400"
                    [pTooltip]="'This metric is not affected by date filtering'"
                    i18n-pTooltip
                    tooltipPosition="top" i18n>(Not filtered)</span>
            </div>
            <div class="text-3xl font-bold text-gray-900">{{ dashboardData.summaryStats.activeFSRs }}</div>
          </div>
        </div>
      </p-card>

      <!-- Total Reservations -->
      <p-card class="relative" 
              [ngClass]="{'border-2 border-blue-300 shadow-lg': isDateFilterActive}" 
              disabled="true"  >
        <div class="flex items-center justify-between">
          <div>
            <div class="flex items-center gap-2 mb-2">
              <i class="pi pi-calendar-plus text-yellow-500 text-xl" [pTooltip]="'Reservation metrics'" i18n-pTooltip></i>
              <span class="text-sm font-medium text-gray-600" i18n>Total Reservations</span>
              <i *ngIf="isDateFilterActive" 
                 class="pi pi-filter text-yellow-500 text-xs ml-1" 
                 [pTooltip]="'Results filtered by selected date range'"
                 i18n-pTooltip
                 tooltipPosition="top"></i>
            </div>
            <div class="text-3xl font-bold text-gray-900">{{ dashboardData.summaryStats.totalReservations }}</div>
          </div>
        </div>
      </p-card>

      <!-- Total Contract Value -->
      <p-card class="kpi-card cursor-pointer relative" 
              [ngClass]="{'border-2 border-blue-300 shadow-lg': isDateFilterActive}" 
              (click)="navigateToContracts()"
              [pTooltip]="'Total value of Active and Draft contracts. Click to view detailed contract information and analytics.'"
              i18n-pTooltip
              tooltipPosition="top">
        <div class="absolute top-3 right-3 z-10" [pTooltip]="'Clickable'" i18n-pTooltip tooltipPosition="top">
          <div class="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center border border-blue-300 shadow-sm hover:bg-blue-200 transition-colors cursor-default">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#2563EB" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 1.07V9h7c0-4.08-3.05-7.44-7-7.93zM4 15c0 4.42 3.58 8 8 8s8-3.58 8-8v-4H4v4zm7-13.93C7.05 1.56 4 4.92 4 9h7V1.07z"/>
            </svg>
          </div>
        </div>
        <div class="flex items-center justify-between">
          <div>
            <div class="flex items-center gap-2 mb-2">
              <i class="pi pi-euro text-purple-500 text-xl" [pTooltip]="'Contract value metrics'" i18n-pTooltip></i>
              <span class="text-sm font-medium text-gray-600" i18n>Total Contract Value</span>
              <i *ngIf="isDateFilterActive" 
                 class="pi pi-filter text-purple-500 text-xs ml-1" 
                 [pTooltip]="'Results filtered by selected date range'"
                 i18n-pTooltip
                 tooltipPosition="top"></i>
            </div>
            <div class="text-3xl font-bold text-gray-900">{{ formatCurrency(dashboardData.summaryStats.totalContractValue) }}</div>
          </div>
        </div>
      </p-card>
    </div>
  </div>

  <!-- Charts Row -->
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
    <!-- Loading Skeletons for Charts -->
    <div *ngIf="loading" class="grid grid-cols-1 lg:grid-cols-3 gap-6 col-span-full">
      <!-- Main Chart Skeleton -->
      <div class="lg:col-span-2">
        <p-card>
          <ng-template pTemplate="header">
            <div class="flex justify-between items-center p-4">
              <div class="flex-1">
                <p-skeleton width="16rem" height="1.5rem" styleClass="mb-2"></p-skeleton>
                <p-skeleton width="12rem" height="1rem"></p-skeleton>
              </div>
              <div class="text-right">
                <p-skeleton width="6rem" height="1rem" styleClass="mb-1"></p-skeleton>
                <p-skeleton width="4rem" height="1.5rem"></p-skeleton>
              </div>
            </div>
          </ng-template>
          <div class="chart-container" style="height: 300px;">
            <p-skeleton width="100%" height="300px"></p-skeleton>
          </div>
        </p-card>
      </div>
      
      <!-- Side Chart Skeleton -->
      <div>
        <p-card>
          <ng-template pTemplate="header">
            <div class="p-4">
              <p-skeleton width="10rem" height="1.5rem"></p-skeleton>
            </div>
          </ng-template>
          <div class="chart-container" style="height: 300px;">
            <p-skeleton width="100%" height="300px"></p-skeleton>
          </div>
        </p-card>
      </div>
    </div>
    
    <!-- Actual Charts -->
    <div *ngIf="!loading && dashboardData" class="grid grid-cols-1 lg:grid-cols-3 gap-6 col-span-full">
      <!-- Subscriber Performance Overview Chart -->
      <div class="lg:col-span-2">
        <p-card [ngClass]="{'border-2 border-blue-300': isDateFilterActive}">
          <ng-template pTemplate="header">
            <div class="flex flex-wrap md:flex-nowrap justify-between items-center p-4">
              <div>
                <div class="flex items-center gap-2">
                  <h3 class="text-lg font-semibold" i18n>Performance Overview | Contracts vs Reservations</h3>
                  <i *ngIf="isDateFilterActive" 
                     class="pi pi-filter text-blue-500" 
                     [pTooltip]="'Filtered by ' + dateFilterDescription"
                     i18n-pTooltip
                     tooltipPosition="top"></i>
                  <i class="pi pi-info-circle text-gray-400 cursor-help" 
                     [pTooltip]="'This chart shows all reservations and Active contracts for the selected subscriber within the chosen date range.'"
                     i18n-pTooltip
                     tooltipPosition="top"></i>
                </div>
                <p class="text-sm text-gray-600 mt-1">{{ activeSubscriberName }}</p>
              </div>
              <div class="flex flex-wrap md:flex-nowrap items-center gap-2">
                <div class="text-right text-sm">
                  <div class="font-medium text-gray-700" i18n>Conversion Rate</div>
                  <div class="text-lg font-bold text-blue-600" 
                       [pTooltip]="'Percentage of reservations that converted to contracts'"
                       i18n-pTooltip
                       tooltipPosition="top">{{ getActiveSubscriberData().conversionRate }}%</div>
                </div>
                <p-button appGlobalLoadingDisable label="Details" 
                          icon="pi pi-info-circle" 
                          [text]="true" 
                          size="small" 
                          (onClick)="navigateToContracts()"
                          [pTooltip]="'View detailed contract information'"
                          i18n-label
                          i18n-pTooltip
                          tooltipPosition="top"></p-button>
              </div>
            </div>
          </ng-template>
          <div class="chart-container" style="height: 300px;">
            <p-chart type="bar" [data]="subscriberPerformanceChartData" [options]="subscriberPerformanceChartOptions"></p-chart>
          </div>
        <ng-template pTemplate="footer">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50">
            <div class="text-center">
              <div class="text-xl font-bold text-blue-600">{{ getActiveSubscriberData().reservationCount }}</div>
              <div class="text-sm text-gray-600" i18n>Total Reservations</div>
            </div>
            <div class="text-center">
              <div class="text-xl font-bold text-green-600">{{ getActiveSubscriberData().contractCount }}</div>
              <div class="text-sm text-gray-600" i18n>Active Contracts</div>
            </div>
            <div class="text-center">
              <div class="text-xl font-bold text-purple-600">{{ formatCurrency(getActiveSubscriberData().totalContractValue) }}</div>
              <div class="text-sm text-gray-600" i18n>Contract Value</div>
            </div>
          </div>
        </ng-template>
      </p-card>
    </div>

      <!-- Reservation Status Chart -->
      <div>
        <p-card [ngClass]="{'border-2 border-blue-300': isDateFilterActive}">
          <ng-template pTemplate="header">
            <div class="p-4">
              <div class="flex items-center gap-2">
                <h3 class="text-lg font-semibold" i18n>Reservation Status</h3>
                <i *ngIf="isDateFilterActive" 
                   class="pi pi-filter text-blue-500" 
                   [pTooltip]="'Filtered by ' + dateFilterDescription"
                   i18n-pTooltip
                   tooltipPosition="top"></i>
                <i class="pi pi-info-circle text-gray-400 cursor-help" 
                   [pTooltip]="'Distribution of reservation statuses showing the current state of all reservations in the system.'"
                   i18n-pTooltip
                   tooltipPosition="top"></i>
              </div>
            </div>
          </ng-template>
          <div class="chart-container" style="height: 300px;">
            <p-chart type="doughnut" [data]="reservationStatusChartData" [options]="reservationStatusOptions"></p-chart>
          </div>
        </p-card>
      </div>
    </div>
  </div>

  <!-- Performance Calculation Explanation -->
  <div class="mb-8" *ngIf="dashboardData">
    <p-card>
        <ng-template pTemplate="header">
        <div class="p-4">
          <h3 class="text-lg font-semibold" i18n>How Performance Score is Calculated</h3>
        </div>
      </ng-template>
      <div class="p-4">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div class="text-center p-3 bg-blue-50 rounded-lg">
            <div class="text-2xl font-bold text-blue-600 mb-1">40%</div>
            <div class="text-sm font-medium text-blue-800" i18n>Conversion Rate</div>
            <div class="text-xs text-blue-600 mt-1" i18n>Reservations → Contracts</div>
          </div>
          <div class="text-center p-3 bg-green-50 rounded-lg">
            <div class="text-2xl font-bold text-green-600 mb-1">30%</div>
            <div class="text-sm font-medium text-green-800" i18n>Activity Level</div>
            <div class="text-xs text-green-600 mt-1" i18n>Total reservations created</div>
          </div>
          <div class="text-center p-3 bg-purple-50 rounded-lg">
            <div class="text-2xl font-bold text-purple-600 mb-1">20%</div>
            <div class="text-sm font-medium text-purple-800" i18n>Customer Base</div>
            <div class="text-xs text-purple-600 mt-1" i18n>Unique customers served</div>
          </div>
          <div class="text-center p-3 bg-orange-50 rounded-lg">
            <div class="text-2xl font-bold text-orange-600 mb-1">10%</div>
            <div class="text-sm font-medium text-orange-800" i18n>Territory Coverage</div>
            <div class="text-xs text-orange-600 mt-1" i18n>Assigned territories</div>
          </div>
        </div>
        <div class="text-sm text-gray-600 text-center" i18n>
          <i class="pi pi-info-circle mr-2"></i>
          Performance scores are calculated based on a weighted combination of key metrics. 
          Higher scores indicate better overall performance across all areas.
        </div>
      </div>
    </p-card>
  </div>

  <!-- Bottom Row -->
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" *ngIf="dashboardData">
    <!-- Latest Customers -->
    <div>
      <p-card>
        <ng-template pTemplate="header">
          <div class="flex justify-between items-center p-4">
            <h3 class="text-lg font-semibold" i18n>Latest Customers</h3>
            <p-button appGlobalLoadingDisable label="View All" [text]="true" size="small" (onClick)="navigateToCustomers()" i18n-label></p-button>
          </div>
        </ng-template>
        <div class="space-y-4">
          <div *ngFor="let customer of dashboardData.recentCustomers" class="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer" (click)="navigateToCustomer(customer._id)">
            <p-avatar 
              [label]="getBusinessInitials(customer.businessName)" 
              size="normal" 
              shape="circle"
              [style]="{'background-color': '#3B82F6', 'color': '#FFFFFF'}"></p-avatar>
            <div class="flex-1">
              <div class="font-medium">{{ customer.businessName }}</div>
              <div class="text-sm text-gray-500" i18n>Added by {{ customer.addedBy }} : {{ formatDate(customer.addedAt) }}</div>
            </div>
            <i class="pi pi-chevron-right text-gray-400"></i>
          </div>
        </div>
      </p-card>
    </div>

    <!-- Top Performing FSRs -->
    <div>
      <p-card>
        <ng-template pTemplate="header">
          <div class="flex justify-between items-center p-4">
            <h3 class="text-lg font-semibold" i18n>Top Performing FSRs</h3>
            <p-button appGlobalLoadingDisable label="View All" [text]="true" size="small" (onClick)="navigateToUsers()" i18n-label></p-button>
          </div>
        </ng-template>
        <div class="space-y-4">
          <div *ngFor="let fsr of dashboardData.topPerformingFSRs" class="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer" (click)="navigateToUsers()">
            <p-avatar 
              [label]="getInitials(fsr.firstName, fsr.lastName)" 
              size="normal" 
              shape="circle"
              [style]="{'background-color': getPerformanceScoreColor(calculatePerformanceScore(fsr)), 'color': '#FFFFFF'}"></p-avatar>
            <div class="flex-1">
              <div class="font-medium">{{ fsr.firstName }} {{ fsr.lastName }}</div>
              <div class="text-sm text-gray-500" i18n>{{ calculatePerformanceScore(fsr) }}% Performance Score</div>
            </div>
            <i class="pi pi-star-fill text-yellow-500"></i>
          </div>
        </div>
        
        <!-- Performance Score Calculation Info -->
        <!-- <div class="mt-4 p-3 bg-gray-50 rounded-lg border-t">
          <div class="text-xs font-medium text-gray-700 mb-2">Performance Score Calculation:</div>
          <div class="grid grid-cols-2 gap-2 text-xs text-gray-600">
            <div class="flex justify-between">
              <span>Conversion Rate:</span>
              <span class="font-medium">40%</span>
            </div>
            <div class="flex justify-between">
              <span>Activity Level:</span>
              <span class="font-medium">30%</span>
            </div>
            <div class="flex justify-between">
              <span>Customer Base:</span>
              <span class="font-medium">20%</span>
            </div>
            <div class="flex justify-between">
              <span>Territory Coverage:</span>
              <span class="font-medium">10%</span>
            </div>
          </div>
          <div class="text-xs text-gray-500 mt-2 text-center">
            <i class="pi pi-info-circle mr-1"></i>
            Weighted average of key performance metrics
          </div>
        </div> -->
      </p-card>
    </div>

    <!-- Recent Notifications -->
    <div>
      <p-card>
        <ng-template pTemplate="header">
          <div class="flex justify-between items-center p-4">
            <h3 class="text-lg font-semibold" i18n>Recent Notifications</h3>
            <p-button appGlobalLoadingDisable label="View All" [text]="true" size="small" (onClick)="navigateToNotifications()" i18n-label></p-button>
          </div>
        </ng-template>
        <div *ngIf="dashboardData && dashboardData.notifications && dashboardData.notifications.length > 0" class="space-y-3 p-4">
          <div *ngFor="let notification of dashboardData.notifications" 
               class="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer border-l-4"
               [ngClass]="{
                 'border-l-blue-500': notification.type === 'info',
                 'border-l-yellow-500': notification.type === 'warning',
                 'border-l-red-500': notification.type === 'error',
                 'border-l-green-500': notification.type === 'success'
               }"
               (click)="navigateToNotifications()">
            <div class="flex-shrink-0 mt-1">
              <i class="pi pi-bell text-lg"
                 [ngClass]="{
                   'text-blue-500': notification.type === 'info',
                   'text-yellow-500': notification.type === 'warning',
                   'text-red-500': notification.type === 'error',
                   'text-green-500': notification.type === 'success'
                 }"></i>
            </div>
            <div class="flex-1 min-w-0">
              <div class="font-medium text-gray-900 mb-1">{{ notification.title }}</div>
              <div class="text-sm text-gray-600 line-clamp-2">{{ notification.message }}</div>
              <div class="text-xs text-gray-400 mt-1">{{ formatDate(notification.createdAt) }}</div>
            </div>
            <div class="flex-shrink-0" *ngIf="!notification.read">
              <span class="inline-flex items-center justify-center w-2 h-2 bg-blue-500 rounded-full"></span>
            </div>
          </div>
        </div>
        <div *ngIf="!dashboardData || !dashboardData.notifications || dashboardData.notifications.length === 0" class="p-4 text-center text-gray-500">
          <i class="pi pi-bell text-4xl mb-2"></i>
          <div i18n>No new notifications</div>
          <p-button appGlobalLoadingDisable label="View All Notifications" [text]="true" size="small" (onClick)="navigateToNotifications()" class="mt-2" i18n-label></p-button>
        </div>
      </p-card>
    </div>
  </div>

  <!-- Territories -->
  <div *ngIf="dashboardData">
    <p-card>
      <ng-template pTemplate="header">
        <div class="flex justify-between items-center p-4">
          <h3 class="text-lg font-semibold" i18n>Territories</h3>
          <p-button appGlobalLoadingDisable label="View All On Map" [text]="true" size="small" (onClick)="navigateToTerritories()" i18n-label></p-button>
        </div>
      </ng-template>
      <div class="flex flex-wrap gap-2 p-4">
        <p-tag *ngFor="let territory of dashboardData.territories.slice(0, 5)" 
               [value]="territory.name" 
               [severity]="getStatusSeverity(territory.status)">
        </p-tag>
        <p-tag *ngIf="dashboardData.territories.length > 5" 
               [value]="'+' + (dashboardData.territories.length - 5) + ' More'" 
               severity="info">
        </p-tag>
      </div>
    </p-card>
  </div>
  </div> <!-- End of main dashboard content -->
</div>

<!-- <app-terms-conditions-modal
[visible]="true"
>
</app-terms-conditions-modal> -->